/**
 * PartNoLabel — canonical "JP + alias subline" renderer for the whole app.
 *
 * What it shows:
 *   Line 1: <new JP code>   (mono blue by default; configurable via className)
 *   Line 2: "Ref: <OE-bare>"   if oe_part_no exists, formatted via formatOeBare
 *           else "Old: <legacy_jp_part_no>"  if legacy differs from JP
 *           else nothing (no dash)
 *
 * Why this exists:
 * Operators repeatedly report that bare JP codes (JPSEV0109, JPSEV1828, …)
 * aren't recognisable on their own — they need either the OE ref ("319593-(ID)")
 * or the legacy code ("JMB035279P") to identify what the part actually is.
 * formatJpWithAlias() does this single-line; this component handles the
 * two-line layout used in cards / table rows / search results / modal headers.
 *
 * Robust to incomplete data: if the passed item only has {jp_part_no}, the
 * component lazily hydrates oe_part_no & legacy_jp_part_no via
 * usePartAliasCache(). So any backend route returning bare items will
 * automatically render the alias subline once the cache fills.
 *
 * Props:
 *   item     — object with at least jp_part_no (or new_jp_part_no). May
 *              optionally carry oe_part_no, legacy_jp_part_no, old_jp_part_no,
 *              new_jp_part_no — those win over the cache.
 *   jp       — alternative to `item`: just the JP code as a string.
 *   inline   — when true, render JP and subline inline (single line:
 *              "JPSEV1796 · Ref: 200863-(MB)"). Default false (two lines).
 *   size     — 'xs' | 'sm' (default) | 'md'
 *   showJp   — render the JP line (default true). Pass false to render only
 *              the subline next to a JP rendered elsewhere.
 *   className — extra classes for the wrapper.
 *   jpClassName — extra classes for the JP line only.
 *   subClassName — extra classes for the subline only.
 *   testId   — optional data-testid prefix.
 */
import React from 'react';
import { formatOeBare } from '../utils/partDisplay';
import { usePartAliasCache, readPartAliasCache } from '../hooks/usePartAliasCache';

function pickAliases(item, hydrated) {
  // Caller-supplied fields win — they reflect the freshest payload.
  const oe = item?.oe_part_no || hydrated?.oe_part_no || '';
  const legacy = item?.legacy_jp_part_no
              || item?.old_jp_part_no
              || hydrated?.legacy_jp_part_no
              || hydrated?.old_jp_part_no
              || '';
  return { oe, legacy };
}

function isMeaningfulOe(oe) {
  if (!oe) return false;
  const s = String(oe).trim();
  if (!s || s === '-') return false;
  if (s.toLowerCase() === 'not available' || s.toLowerCase() === 'n/a') return false;
  return true;
}

const SIZE_CLASSES = {
  xs: { jp: 'text-[11px]', sub: 'text-[10px]' },
  sm: { jp: 'text-sm', sub: 'text-[11px]' },
  md: { jp: 'text-base', sub: 'text-xs' },
};

export const PartNoLabel = ({
  item,
  jp,
  inline = false,
  size = 'sm',
  showJp = true,
  preferLegacy = false,
  className = '',
  jpClassName = '',
  subClassName = '',
  testId,
}) => {
  // Resolve the canonical JP to display (item.new_jp_part_no preferred,
  // falls back to jp_part_no or the raw `jp` prop).
  const display =
    (item && (item.new_jp_part_no || item.jp_part_no))
    || (jp ? String(jp) : '');

  // Lazy-hydrate aliases when we don't have the relevant alias on the item.
  // - Default mode: hydrate when OE is missing (OE is preferred subline).
  // - preferLegacy mode (used in tables that show OE in its own column):
  //   hydrate when LEGACY is missing.
  const needHydrate = !!display && (
    preferLegacy
      ? !(item && (item.legacy_jp_part_no || item.old_jp_part_no))
      : !(item && item.oe_part_no)
  );
  const hydratedMap = usePartAliasCache(needHydrate ? [display] : []);
  const hydrated = hydratedMap[display] || readPartAliasCache(display) || {};

  const { oe, legacy } = pickAliases(item || {}, hydrated);

  let subline = '';
  let kind = null; // 'ref' | 'old'
  if (preferLegacy) {
    // Tables/PDFs that show OE in its own column → show LEGACY next to JP.
    if (legacy && legacy !== display) {
      subline = `(${legacy})`;
      kind = 'old';
    }
  } else {
    if (isMeaningfulOe(oe)) {
      const bare = formatOeBare(oe);
      if (bare && bare !== 'N/A') {
        subline = `Ref: ${bare}`;
        kind = 'ref';
      }
    }
    if (!subline && legacy && legacy !== display) {
      subline = `Old: ${legacy}`;
      kind = 'old';
    }
  }

  const sz = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const subColor = kind === 'ref' ? 'text-blue-600' : 'text-amber-700';

  if (!display) return null;

  if (inline) {
    return (
      <span className={`inline-flex items-baseline gap-1.5 ${className}`} data-testid={testId}>
        {showJp && (
          <span
            className={`font-mono font-semibold ${sz.jp} ${jpClassName || 'text-blue-700'}`}
            data-testid={testId ? `${testId}-jp` : undefined}
          >
            {display}
          </span>
        )}
        {subline && (
          <span
            className={`${sz.sub} ${subColor} ${subClassName}`}
            data-testid={testId ? `${testId}-sub` : undefined}
          >
            {showJp ? '·' : ''} {subline}
          </span>
        )}
      </span>
    );
  }

  // Two-line layout (default).
  return (
    <span className={`inline-block leading-tight ${className}`} data-testid={testId}>
      {showJp && (
        <span
          className={`block font-mono font-semibold ${sz.jp} ${jpClassName || 'text-blue-700'}`}
          data-testid={testId ? `${testId}-jp` : undefined}
        >
          {display}
        </span>
      )}
      {subline && (
        <span
          className={`block ${sz.sub} ${subColor} ${subClassName}`}
          data-testid={testId ? `${testId}-sub` : undefined}
        >
          {subline}
        </span>
      )}
    </span>
  );
};

export default PartNoLabel;
